import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import axios from 'axios'
import { authService } from '../api/services/auth.service'
import { authStorage } from '../lib/auth-storage'
import { hasPermission, resolveDashboardPath, ROLE_LABELS, canReviewDoctorProfiles, canUseCasePermission, USER_ROLES, type AuthSession, type UserRoleSlug } from '../types/auth'

type AuthContextValue = {
  session: AuthSession | null
  isAuthenticated: boolean
  isLoading: boolean
  roleLabel: string
  dashboardPath: string
  setSession: (session: AuthSession) => void
  refreshSession: () => Promise<AuthSession | null>
  logout: () => Promise<void>
  can: (permission: string) => boolean
  /** Permission + can_review_profiles (for profile reviewers). */
  canReviewDoctors: (permission?: string) => boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

type AuthProviderProps = {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSessionState] = useState<AuthSession | null>(() => authStorage.getSession())
  const [isLoading, setIsLoading] = useState(false)

  const setSession = useCallback((nextSession: AuthSession) => {
    authStorage.setSession(nextSession)
    setSessionState(nextSession)
  }, [])

  const refreshSession = useCallback(async () => {
    if (!authStorage.getToken()) {
      authStorage.clear()
      setSessionState(null)

      return null
    }

    setIsLoading(true)

    try {
      const response = await authService.me()
      const nextSession: AuthSession = {
        ...response.data,
        token: authStorage.getToken() ?? undefined,
      }

      setSession(nextSession)

      return nextSession
    } catch (error) {
      // Only clear the session on true auth failures. Rate limits (429) and
      // transient errors must not look like a logout.
      const status = axios.isAxiosError(error) ? error.response?.status : undefined

      if (status === 401) {
        authStorage.clear()
        setSessionState(null)
      }

      return null
    } finally {
      setIsLoading(false)
    }
  }, [setSession])

  const logout = useCallback(async () => {
    try {
      await authService.logout()
    } finally {
      authStorage.clear()
      setSessionState(null)
    }
  }, [])

  const value = useMemo<AuthContextValue>(() => {
    const role = session?.role ?? null
    const roleSlug = role as UserRoleSlug | null

    return {
      session,
      isAuthenticated: Boolean(session && authStorage.getToken()),
      isLoading,
      roleLabel: roleSlug ? ROLE_LABELS[roleSlug] ?? role : 'User',
      dashboardPath: session?.dashboard_path ?? resolveDashboardPath(role),
      setSession,
      refreshSession,
      logout,
      can: (permission: string) => {
        // Doctors need Spatie permission AND approved profile status.
        if (
          session?.role === USER_ROLES.DOCTOR &&
          session.user.status !== 'approved'
        ) {
          return false
        }

        // Doctor-profile actions need Spatie permission + can_review_profiles (reviewers).
        if (permission.startsWith('doctors-')) {
          return canReviewDoctorProfiles(session, permission)
        }

        // Case actions for assistants need Spatie permission + doctor pivot flags.
        if (
          permission.startsWith('cases-') ||
          permission.startsWith('case-files-') ||
          permission.startsWith('case-comments-') ||
          permission.startsWith('case-activity-')
        ) {
          return canUseCasePermission(session, permission)
        }

        return hasPermission(session?.permissions ?? [], permission)
      },
      canReviewDoctors: (permission = 'doctors-list') => canReviewDoctorProfiles(session, permission),
    }
  }, [isLoading, logout, refreshSession, session, setSession])

  useEffect(() => {
    if (authStorage.getToken()) {
      void refreshSession()
    }
  }, [refreshSession])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }

  return context
}
